import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SEO } from "@/components/seo";
import { CheckCircle2, TrendingUp, HelpCircle, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth-context";

const monthlyPlans = [
  {
    name: "Starter",
    price: "Free",
    period: "",
    description: "Perfect for learning and paper trading",
    features: [
      "Paper trading only",
      "3 active trading rules",
      "Basic market data",
      "Community support",
      "Email support",
    ],
    limitations: [
      "No live trading",
      "Limited rule conditions",
    ],
    cta: "Get Started",
    popular: false,
  },
  {
    name: "Pro",
    price: "$49",
    period: "/month",
    description: "For active traders ready to automate",
    features: [
      "Live trading enabled",
      "25 active trading rules",
      "Real-time market data",
      "News & social triggers",
      "Options chain data",
      "Priority email support",
      "Advanced analytics dashboard",
      "Execution history tracking",
    ],
    limitations: [],
    cta: "Start 14-Day Free Trial",
    popular: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    description: "For institutions and professional traders",
    features: [
      "Unlimited trading rules",
      "Dedicated account manager",
      "Custom API integrations",
      "Full API access",
      "White-label options",
      "SLA guarantees",
      "Custom onboarding",
      "Phone support",
    ],
    limitations: [],
    cta: "Contact Sales",
    popular: false,
  },
];

const annualPlans = monthlyPlans.map(plan => ({
  ...plan,
  price: plan.price === "Free" ? "Free" : plan.price === "Custom" ? "Custom" : "$39",
  period: plan.period ? "/month" : "",
  annualNote: plan.price !== "Free" && plan.price !== "Custom" ? "Billed annually at $468" : undefined,
}));

const faqs = [
  {
    question: "What does TradePro do?",
    answer: "TradePro lets you create, test, and run live trading algorithms with just a few clicks. Trade stocks, options, and crypto with speed and precision. We've removed the technical barriers so algorithmic trading isn't just for engineers and big institutions.",
  },
  {
    question: "Do I need to know how to code?",
    answer: "Absolutely not! TradePro is designed for traders of all skill levels. Our no-code rule builder lets you create custom trading algorithms in minutes without writing a single line of code.",
  },
  {
    question: "How do I get started?",
    answer: "Sign up for a free account, complete the account application, and fund your account. You can start with paper trading immediately to test your strategies risk-free.",
  },
  {
    question: "What brokers do you support?",
    answer: "We support major brokers including Alpaca, Tradier, Interactive Brokers, and cryptocurrency exchanges like Coinbase and Kraken. More integrations are added regularly.",
  },
  {
    question: "Is my money safe?",
    answer: "Your funds are held at your chosen brokerage, not with TradePro. We use bank-grade encryption and never have direct access to your money. All supported brokers are regulated and SIPC/FDIC insured.",
  },
  {
    question: "Can I cancel anytime?",
    answer: "Yes! You can cancel your subscription at any time. If you cancel, you'll retain access until the end of your billing period.",
  },
];

export default function PricingPage() {
  const { isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Pricing - TradePro | Automated Trading Plans"
        description="Choose the perfect TradePro plan for your trading needs. Free starter plan, Pro at $49/mo with unlimited rules, or custom Enterprise solutions."
      />
      <header className="fixed top-0 left-0 right-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link href="/" data-testid="link-home-logo">
            <div className="flex items-center gap-2 cursor-pointer">
              <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">TradePro</span>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6" data-testid="nav-main">
            <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="link-nav-features">Features</Link>
            <Link href="/pricing" className="text-sm text-foreground font-medium" data-testid="link-nav-pricing">Pricing</Link>
            <Link href="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="link-nav-about">About</Link>
          </nav>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <Link href="/dashboard">
                <Button data-testid="button-go-to-dashboard">Go to Dashboard</Button>
              </Link>
            ) : (
              <>
                <Button variant="ghost" data-testid="button-login" onClick={() => setLocation("/login")}>Log in</Button>
                <Button data-testid="button-get-started" onClick={() => setLocation("/login")}>Get Started</Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="pt-24 pb-20">
        <section className="px-4 mb-16">
          <div className="container mx-auto text-center max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Simple, Transparent Pricing</h1>
            <p className="text-xl text-muted-foreground">
              Choose the plan that fits your trading needs. Start free and upgrade when you're ready.
            </p>
          </div>
        </section>

        <section className="px-4 mb-20">
          <div className="container mx-auto max-w-5xl">
            <Tabs defaultValue="monthly" className="w-full">
              <div className="flex justify-center mb-8">
                <TabsList>
                  <TabsTrigger value="monthly" data-testid="tab-pricing-monthly">Monthly</TabsTrigger>
                  <TabsTrigger value="annual" data-testid="tab-pricing-annual">Annual (20% off)</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="monthly">
                <div className="grid md:grid-cols-3 gap-8">
                  {monthlyPlans.map((plan) => (
                    <Card 
                      key={plan.name} 
                      className={`relative ${plan.popular ? 'border-primary shadow-lg' : ''}`}
                      data-testid={`pricing-plan-${plan.name.toLowerCase()}-monthly`}
                    >
                      {plan.popular && (
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                          Most Popular
                        </Badge>
                      )}
                      <CardHeader className="text-center pb-2">
                        <CardTitle className="text-xl">{plan.name}</CardTitle>
                        <CardDescription>{plan.description}</CardDescription>
                        <div className="pt-4">
                          <span className="text-4xl font-bold">{plan.price}</span>
                          <span className="text-muted-foreground">{plan.period}</span>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <ul className="space-y-3">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                              <span className="text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                      <CardFooter>
                        <div className="w-full">
                          <Button 
                            className="w-full" 
                            variant={plan.popular ? "default" : "outline"}
                            data-testid={`button-plan-${plan.name.toLowerCase()}-monthly`}
                            onClick={() => setLocation("/login")}
                          >
                            {plan.cta}
                          </Button>
                        </div>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="annual">
                <div className="grid md:grid-cols-3 gap-8">
                  {annualPlans.map((plan) => (
                    <Card 
                      key={plan.name} 
                      className={`relative ${plan.popular ? 'border-primary shadow-lg' : ''}`}
                      data-testid={`pricing-plan-${plan.name.toLowerCase()}-annual`}
                    >
                      {plan.popular && (
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                          Most Popular
                        </Badge>
                      )}
                      <CardHeader className="text-center pb-2">
                        <CardTitle className="text-xl">{plan.name}</CardTitle>
                        <CardDescription>{plan.description}</CardDescription>
                        <div className="pt-4">
                          <span className="text-4xl font-bold">{plan.price}</span>
                          <span className="text-muted-foreground">{plan.period}</span>
                        </div>
                        {plan.annualNote && (
                          <p className="text-xs text-muted-foreground mt-2">{plan.annualNote}</p>
                        )}
                      </CardHeader>
                      <CardContent className="pt-4">
                        <ul className="space-y-3">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                              <span className="text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                      <CardFooter>
                        <div className="w-full">
                          <Button 
                            className="w-full" 
                            variant={plan.popular ? "default" : "outline"}
                            data-testid={`button-plan-${plan.name.toLowerCase()}-annual`}
                            onClick={() => setLocation("/login")}
                          >
                            {plan.cta}
                          </Button>
                        </div>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        <section className="px-4 py-20 bg-muted/30">
          <div className="container mx-auto max-w-3xl">
            <div className="text-center mb-12">
              <HelpCircle className="h-10 w-10 mx-auto mb-4 text-primary" />
              <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
            </div>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} data-testid={`faq-${index}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 px-4">
          <div className="container mx-auto text-center max-w-2xl">
            <h2 className="text-3xl font-bold mb-4">Ready to Start Trading?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join thousands of traders automating their strategies with TradePro.
            </p>
            <Link href="/open-account">
              <Button size="lg" data-testid="button-open-account">
                Open New Account
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t py-8 px-4">
        <div className="container mx-auto text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} TradePro. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
